import { useSignal } from "@preact/signals-react";
import { isString } from "lodash-es";
import { useEffect } from "react";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { getTypename } from "../../library/metadataUtils";
import { INGImageProps } from "../../library/NGFieldExtensions";
import { getClassName, getTestId, getsxObject, isNullOrEmpty, partitionMUIClasses } from "../../library/utils";
import { convertToSizedImageFilename, setupImageDetails } from "../ComponentUtils";
import NGIcon from "../NGIcon/NGIcon";
import { MUIImage } from "./MUIImage";
import { MUIImageProps } from "./MUIImage.types";

const tag = "NGImage";

export default function NGImage({ config, context }: INGImageProps) {
  const testId = getTestId(config);
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? ""),
      FallbackImage: useSignal(config.FallbackImage ?? ""),
      Visible: useSignal(config.Visible ?? true),
      AlternativeText: useSignal(config.AlternativeText || ""),
      ErrorIcon: useSignal(config.ErrorIcon || ""),
      Size: useSignal(config.Size || "Medium"),
      Path: useSignal(config.Path || ""),
      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Source: useSignal(config.Source ?? null), // Asset, Vault, External
    },
    context
  );

  log.info(tag, `render ${local.Value?.value} `);

  const url = useSignal("");

  useEffect(() => {
    log.info(tag, `use effect ${local.Value?.value} `);
    // if not visible, do not process.
    if (!local.Visible.value) return;

    if (!isString(local.Value.value)) {
      url.value = "";
      return;
    }
    // setup for local.Value.value, if error happens, onError will be called for fallback image
    setupImageDetails(local, local.Value.value, url, convertToSizedImageFilename);
  }, [local.Visible.value, local.Value.value, local.Path.value, local.Size.value, local.Source.value]);

  const handlers = setupHandlers(config, context);

  return (
    <>
      {local.Visible.value && url.value && (
        <MUIImage
          data-testid={testId}
          data-type={getTypename(config)}
          sx={{ display: local.Visible.value ? "block" : "none" }} // impprtant for no gap.
          // sx={getsxObject(local.Style.value)}
          wrapperStyle={getsxObject(local.Style.value)}
          className={getClassName(local.Classes)}
          src={url.value}
          alt={config.AlternativeText as string}
          bgColor={config.BgColor as string}
          distance={config.Distance as string}
          duration={config.Duration ?? 0}
          easing={config.Easing ?? ""}
          errorIcon={
            local.ErrorIcon.value &&
            !isNullOrEmpty(local.ErrorIcon.value.IconName) && (
              <NGIcon
                config={{ Id: `${config.Id}_ErrorIcon`, ...local.ErrorIcon.value, ContextId: testId }}
                context={context}
              />
            )
          }
          fit={config.Fit as MUIImageProps["fit"]}
          iconWrapperClassName={config.IconWrapperClassName as string}
          position={config.Position as MUIImageProps["position"]}
          shift={config.Shift as MUIImageProps["shift"]}
          shiftDuration={config.ShiftDuration as number}
          showLoading={config.ShowLoading as boolean}
          onError={(e) => {
            if (!isNullOrEmpty(local.FallbackImage.value)) {
              setupImageDetails(local, local.FallbackImage.value, url, convertToSizedImageFilename);
            }
            if (config.HideOnError) local.Visible.value = false;
            if ((handlers as any).onError) (handlers as any).onError(e);
          }}
          {...handlers}
          onClick={(e) => {
            const [_, classes] = partitionMUIClasses(local.Classes.value);
            const action = publishUserAction(config, context, "image_click", null, {
              Value: local.Value.value,
              EventCategory: "engagement",
              ElementClass: classes.join(" "),
            });
            if ((handlers as any).onClick) (handlers as any).onClick(e, local.Value.value, action);
          }}
        />
      )}
    </>
  );
}
